// frontend/src/components/quiz/QuizCard.js
import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Trophy } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import ContractBadge from "../common/ContractBadge";
import QuizTypeBadge from "./QuizTypeBadge";
import { getQuizTranslation } from "../../utils/quizTranslations";

const QuizCard = ({ quiz, showProgress = true }) => {
  const { language, t } = useLanguage();

  const getProgressColor = () => {
    if (!quiz.userStats) return "text-gray-400";
    if (quiz.userStats.passed) return "text-green-500";
    return "text-yellow-500";
  };

  const getProgressIcon = () => {
    if (!quiz.userStats) return <Circle className="w-5 h-5" />;
    if (quiz.userStats.passed) return <CheckCircle2 className="w-5 h-5" />;
    return <Circle className="w-5 h-5 fill-current" />;
  };

  return (
    <Link to={`/quiz/${quiz.slug}`}>
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group h-full flex flex-col">
        {/* Image */}
        <div className="relative h-48 bg-gray-200 overflow-hidden">
          <img
            src={quiz.imageUrl}
            alt={t(quiz.title)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = "/images/placeholder-quiz.jpg";
            }}
          />

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

          {/* Quiz type badge */}
          <div className="absolute top-3 left-3">
            <QuizTypeBadge quizType={quiz.quiz_type} />
          </div>

          {/* Contract badge */}
          <div className="absolute top-3 right-3">
            <ContractBadge contract={quiz.contract} />
          </div>

          {/* User progress indicator */}
          {showProgress && quiz.userStats && (
            <div
              className={`absolute bottom-3 right-3 ${getProgressColor()}`}
            >
              {getProgressIcon()}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="text-lg font-semibold text-[#002855] group-hover:text-[#EE005F] transition-colors line-clamp-2 mb-2">
            {t(quiz.title)}
          </h3>

          {quiz.description && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {t(quiz.description)}
            </p>
          )}

          {/* Stats */}
          <div className="mt-auto space-y-2">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                {quiz.questionCount}{" "}
                {getQuizTranslation("ui.questions", language)}
              </span>
              <span>
                {getQuizTranslation("ui.passingScore", language)}:{" "}
                {quiz.passingScore}%
              </span>
            </div>

            {/* User score */}
            {showProgress && quiz.userStats && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  {quiz.userStats.passed && (
                    <Trophy className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="text-sm font-medium text-gray-700">
                    {getQuizTranslation("ui.yourScore", language)}:{" "}
                    {quiz.userStats.bestScore}%
                  </span>
                </div>
                {quiz.userStats.passed && (
                  <span className="text-xs text-green-600 font-medium">
                    {getQuizTranslation("results.passed", language)}
                  </span>
                )}
              </div>
            )}

            {/* Access indicator */}
            {!quiz.hasAccess && (
              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs text-[#EE005F] font-medium">
                  {getQuizTranslation("messages.accessDenied", language)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default QuizCard;
